import React from "react"
import PropTypes from "prop-types"
import { useSortable } from "@dnd-kit/sortable"
import { CSS } from "@dnd-kit/utilities"
import { DeleteOutlined, HolderOutlined } from "@ant-design/icons"
import { Flex, Spin } from "antd"
import { useTranslation } from "react-i18next"
import { useNewConfirmModal } from "modules/modals/hooks/useNewConfirmModal"
import { CHART_TYPES } from "../../constants"
import { getWidgetData, getWidgetMeta } from "../../utils"
import { GenericChartWidget } from "../../widgets"
import styles from "../../style.module.css"

const WIDGET_HEIGHT_OVERRIDE = {
  "annual-summary": styles.widgetTall,
  "booking-availability": styles.widgetCompact
}

export default function SortableWidget({
  widget,
  dashboardData,
  period,
  dateRange,
  isEditing,
  onDelete,
  widgetComponents,
  widgetDefaultChartTypes,
  loading,
}) {
  const { t } = useTranslation()
  const { showConfirm, modal: confirmModal } = useNewConfirmModal()

  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({ id: widget.id })

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    gridColumn: `span ${widget.colSpan ?? 1}`,
    gridRow: `span ${widget.rowSpan ?? 1}`,
    opacity: isDragging ? 0.4 : 1,
    zIndex: isDragging ? 100 : "auto"
  }

  // Use localId (assigned after data arrives) for data/component lookups;
  // fall back to id for backwards-compat with non-placeholder widgets
  const dataKey = widget.localId ?? widget.id

  const entryChartType = dashboardData?.[dataKey]?.chart_type
  const isScrollable = [CHART_TYPES.LINE, CHART_TYPES.LIST].includes(entryChartType)

  const handleDeleteRequest = () => {
    showConfirm(
      {
        title: t("ai_dashboard.delete_widget_title"),
        message: widget.isDefault
          ? t("ai_dashboard.delete_default_widget_message")
          : t("ai_dashboard.delete_widget_message"),
        confirmLabel: t("ai_dashboard.delete")
      },
      () => onDelete(widget.apiId)
    )
  }

  const renderContent = () => {
    if (loading) {
      return (
        <Flex align="center" justify="center" className={styles.widgetSpinner}>
          <Spin size="large" />
        </Flex>
      )
    }

    if (widget.type === "api-dynamic") {
      return <GenericChartWidget data={dashboardData?.[dataKey]} period={period} dateRange={dateRange} />
    }
    const entry = dashboardData?.[dataKey]
    const chartType = entry?.chart_type
    const WidgetComponent = widgetComponents[dataKey]
    if (chartType && WidgetComponent) {
      const defaultType = widgetDefaultChartTypes[dataKey] ?? null
      if (chartType !== defaultType) {
        return <GenericChartWidget data={entry} period={period} dateRange={dateRange} />
      }
    }
    if (!WidgetComponent) {
      return <GenericChartWidget data={getWidgetData(dataKey, dashboardData)} period={period} dateRange={dateRange} />
    }
    return (
      <WidgetComponent
        data={getWidgetData(dataKey, dashboardData)}
        period={period}
        dateRange={dateRange}
        {...getWidgetMeta(dataKey, dashboardData)}
      />
    )
  }

  return (
    <div
      ref={setNodeRef}
      style={style}
      className={`${styles.widget} ${WIDGET_HEIGHT_OVERRIDE[dataKey] || ""} ${isScrollable ? styles.widgetScrollable : ""} ${isDragging ? styles.dragging : ""}`}
    >
      {isEditing && !loading && (
        <Flex align="center" justify="flex-end" gap="var(--spacing-xs)" className={styles.widgetEditControls}>
          <button
            onClick={handleDeleteRequest}
            className={styles.widgetDeleteBtn}
          >
            <DeleteOutlined />
          </button>
          <div className={styles.dragHandle} {...attributes} {...listeners} title={t("ai_dashboard.drag_to_reorder")}>
            <HolderOutlined />
          </div>
        </Flex>
      )}
      {confirmModal}
      {renderContent()}
    </div>
  )
}

SortableWidget.propTypes = {
  widget: PropTypes.shape({
    id: PropTypes.string,
    apiId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    localId: PropTypes.string,
    colSpan: PropTypes.number,
    rowSpan: PropTypes.number,
    type: PropTypes.string,
    isDefault: PropTypes.bool,
  }).isRequired,
  dashboardData: PropTypes.object,

  period: PropTypes.string,
  dateRange: PropTypes.array,
  isEditing: PropTypes.bool,
  onDelete: PropTypes.func,
  widgetComponents: PropTypes.object.isRequired,
  widgetDefaultChartTypes: PropTypes.object.isRequired,
  loading: PropTypes.bool,
}
